<template>
  <div class="pa-4 pt-8">
    <v-form ref="clientForm">
      <v-row>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="editableClient.firstname"
            label="First Name"
            :variant="isEditing ? 'outlined' : 'filled'"
            density="compact"
            :readonly="!isEditing"
            :bg-color="isEditing ? 'white' : 'grey-lighten-3'"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="editableClient.lastname"
            label="Last Name"
            :variant="isEditing ? 'outlined' : 'filled'"
            density="compact"
            :readonly="!isEditing"
            :bg-color="isEditing ? 'white' : 'grey-lighten-3'"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="editableClient.phone"
            label="Phone"
            :variant="isEditing ? 'outlined' : 'filled'"
            density="compact"
            :readonly="!isEditing"
            :bg-color="isEditing ? 'white' : 'grey-lighten-3'"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="editableClient.email"
            label="Email"
            :variant="isEditing ? 'outlined' : 'filled'"
            density="compact"
            :readonly="!isEditing"
            :bg-color="isEditing ? 'white' : 'grey-lighten-3'"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-checkbox
            v-if="isEditing"
            v-model="editableClient.marketing"
            label="Email marketing"
            density="compact"
          ></v-checkbox>
          <div v-else class="d-flex align-center py-2">
            <span class="text-body-2 text-grey-darken-1 mr-3">Email Marketing:</span>
            <v-chip
              :color="editableClient.marketing ? 'green' : 'red'"
              variant="flat"
              size="small"
            >
              {{ editableClient.marketing ? 'Yes' : 'No' }}
            </v-chip>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-btn
            v-if="!isEditing"
            color="primary"
            variant="tonal"
            prepend-icon="mdi-pencil"
            @click="startEditing"
          >
            Edit Details
          </v-btn>
          <div v-else>
            <v-btn
              color="primary"
              variant="tonal"
              class="mr-2"
              :loading="saving"
              @click="saveClient"
            >
              Save
            </v-btn>
            <v-btn
              color="red"
              variant="tonal"
              @click="cancelEditing"
            >
              Cancel
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-form>
    <v-snackbar
      v-model="showErrorSnackbar"
      :timeout="5000"
      color="error"
      location="top"
      multi-line
    >
      <div class="text-subtitle-1 pb-2">{{ errorMessage }}</div>
      <ul v-if="errorDetails.length > 0" class="pl-4">
        <li v-for="(error, index) in errorDetails" :key="index">{{ error }}</li>
      </ul>
      <template v-slot:actions>
        <v-btn
          color="white"
          variant="text"
          @click="showErrorSnackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { useClientStore } from "../../../stores/clientStore";

const props = defineProps({
  client: {
    type: Object,
    required: true,
  },
});

const clientStore = useClientStore();
const isEditing = ref(false);
const editableClient = ref({ ...props.client });
const saving = ref(false);
const showErrorSnackbar = ref(false);
const errorMessage = ref('');
const errorDetails = ref([]);

// Watch for client changes
watch(
  () => props.client,
  (newClient) => {
    editableClient.value = { ...newClient };
    isEditing.value = false;
  }
);

const startEditing = () => {
  isEditing.value = true;
  editableClient.value = { ...props.client };
};

const cancelEditing = () => {
  isEditing.value = false;
  editableClient.value = { ...props.client };
};

const saveClient = async () => {
  saving.value = true;
  try {
    await clientStore.updateClient(editableClient.value.id, editableClient.value);
    isEditing.value = false;
  } catch (error) {
    console.error("Error saving client:", error);

    // Display validation errors in snackbar
    if (error.response?.data?.message) {
      errorMessage.value = error.response.data.message;
    } else {
      errorMessage.value = 'Failed to save client details';
    }

    // Format error details from validation errors
    errorDetails.value = [];
    if (error.response?.data?.errors) {
      Object.entries(error.response.data.errors).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          messages.forEach(msg => errorDetails.value.push(`${field}: ${msg}`));
        } else {
          errorDetails.value.push(`${field}: ${messages}`);
        }
      });
    }

    showErrorSnackbar.value = true;
  } finally {
    saving.value = false;
  }
};
</script>
